import type {
  ProductInfoI,
  ProductPageSettings
} from '@routes/ProductPage/index.type';
import { observer } from 'mobx-react';
import type { FC } from 'react';
import React, { useState, useMemo } from 'react';
import {
  Available,
  BestBeforeDate,
  BoughtAmount,
  Delivery,
  DigitalProductDeliveryType,
  IsVesovoiMobile,
  Title,
  TotalRating,
  TotalReviewsCount
} from '../components';
import ProductPageVariants from '../ProductPageVariants';
import ProductsMiniCarousel from '@components/Carousel/ProductsMini';
import { Picture } from '@ui';
import icons from '@icons';
import ModalMapfunc from '@components/Layout/Header/components/InfoHeader/components/ModalMapfunc';
import { useStores } from '@root/src/stores';

const {
  store: { black: storeBlackOutlined }
} = icons.Fill;

type A = Pick<
  ProductPageSettings,
  | 'id'
  | 'reviewTotalCount'
  | 'scrollToReviews'
  | 'isItemInCart'
  | 'alsoBought'
  | 'isShowCarouselAlsoBought'
>;

type B = Pick<
  NonNullable<ProductInfoI>,
  | 'isDigital'
  | 'isExpressDeliveryAval'
  | 'productArticle'
  | 'ifWeighting'
  | 'variants'
  | 'title'
  | 'subtitle'
  | 'averageRating'
  | 'salesNumberInfo'
  | 'available'
  | 'isExpressPickupAval'
  | 'ifWeighting'
  | 'deliveryDate'
  | 'gds_count'
  | 'isProductNotForSale'
  | 'isProductArchieved'
  | 'isPreorder'
  | 'bestBeforeDate'
  | 'newBadges'
>;

type PPMobileMainBlockProps = A &
  B & {
    isBarMenu: boolean;
    availableQuantity?: Array<{
      store_id: number;
      amount: number;
    }>;
    cartStore?: any;
  };

const PPMobileMainBlock: FC<PPMobileMainBlockProps> = observer(
  ({
    id,
    title,
    subtitle,
    isBarMenu,
    productArticle,
    averageRating,
    reviewTotalCount,
    salesNumberInfo,
    available,
    variants,
    gds_count,
    ifWeighting,
    isExpressDeliveryAval,
    isExpressPickupAval,
    deliveryDate,
    isProductNotForSale,
    isProductArchieved,
    isPreorder,
    bestBeforeDate,
    isDigital,
    scrollToReviews,
    isItemInCart,
    alsoBought,
    isShowCarouselAlsoBought,
    newBadges,
    availableQuantity
  }) => {
    const { cartStore } = useStores();
    const [isShowModal, setIsShowModal] = useState(false);

    const filteredStores = useMemo(() => {
      if (!availableQuantity || availableQuantity.length === 0) return [];
      return availableQuantity.filter(item => item.store_id !== 1);
    }, [availableQuantity]);

    const isProductAvailableInStores = useMemo(() => {
      if (!filteredStores || filteredStores.length === 0) return false;
      return filteredStores.some(item => item.amount > 0);
    }, [filteredStores]);

    const handleShowStoresModal = (): void => {
      if (isProductAvailableInStores) {
        setIsShowModal(true);
      }
    };

    const triggerChangePickupStore = (id: any): void => {
      cartStore.updateCartStage({ storeId: id, deliveryId: 5 });
    };

    return (
      <div className='mb-5'>
        {isShowModal && (
          <ModalMapfunc
            changeModalState={setIsShowModal}
            pickups={[]}
            triggerChangePickupStore={triggerChangePickupStore}
            setShowModal={setIsShowModal}
            activeStore={null}
            isHideDelivery={true}
            isShouldAutoSwitchPickupStore={false}
            isShowClusterer
            availableQuantity={availableQuantity}
            isShowAvailableQuantity={true}
          />
        )}

        {!isProductNotForSale && !isProductArchieved && (
          <div className='flex items-center w-full mb-3'>
            <TotalRating rating={averageRating || 0} />
            <TotalReviewsCount
              reviewTotalCount={reviewTotalCount}
              scrollToReviews={scrollToReviews}
            />
            {!isPreorder && (
              <Available gds_count={gds_count || 0} available={!!available} />
            )}
            <BoughtAmount
              count={salesNumberInfo?.[0] || 0}
              postfix={salesNumberInfo?.[1]}
            />
          </div>
        )}

        {isItemInCart && alsoBought.length > 0 && (
          <ProductsMiniCarousel
            title={
              isShowCarouselAlsoBought
                ? 'С этим товаром покупают'
                : 'Вам может понравиться'
            }
            products={alsoBought}
          />
        )}
        <Title
          title={title}
          subtitle={subtitle}
          article={productArticle}
          badges={newBadges}
        />
        {variants?.length ? (
          <div className='flex w-full mb-5 gap-x-1'>
            <ProductPageVariants
              productInfoAvailable
              productId={id}
              variants={variants}
            />
          </div>
        ) : null}

        <div className='flex items-center justify-between mb-3'>
          {ifWeighting && Number(variants?.length) <= 1 && <IsVesovoiMobile />}
          {bestBeforeDate && !isDigital && (
            <BestBeforeDate bestBeforeDate={bestBeforeDate} />
          )}
        </div>

        {!isBarMenu && !isProductNotForSale && !isProductArchieved && (
          <>
            {(ifWeighting || !isPreorder) && (
              <div className='flex items-center justify-between mb-3'>
                {!isDigital && available && (
                  <Delivery
                    isExpressDeliveryAval={!!isExpressDeliveryAval}
                    isExpressPickupAval={!!isExpressPickupAval}
                    deliveryDate={deliveryDate || ''}
                  />
                )}
                {isDigital && <DigitalProductDeliveryType />}
                {!isDigital && isProductAvailableInStores && (
                  <div
                    className='underline cursor-pointer pp-delivery'
                    onClick={handleShowStoresModal}
                  >
                    <Picture icon={storeBlackOutlined} height='20' alt='' />
                    {isExpressDeliveryAval
                      ? 'Самовывоз от 15 мин'
                      : 'Наличие в магазинах'}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

export default PPMobileMainBlock;
